import { Header } from "@/components/dashboard/Header";
import { JournalDrawer } from "@/components/dashboard/JournalDrawer";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { applyFlags } from "@/components/landing/Navbar";
import { Loader } from "@/components/Loader";
import { useLocalStorage } from "@/components/useLocalStorage";
import { useRouteLoading } from "@/components/useRouteLoading";
import { fetcherIgnore404 } from "@/lib/utils";
import { useSession } from "@/components/useSession";
import { JSONSafe, NavItem, User } from "@/types";
import { signIn } from "next-auth/react";
import { useRouter } from "next/router";
import { useEffect } from "react";
import useSWR from "swr";

export const AppShell = ({
  id,
  children,
  menuItem,
  header,
  name,
  project
}: {
  id?: string;
  name?: string;
  children: React.ReactNode;
  menuItem?: Pick<NavItem, "title" | "href">;
  header?: boolean;
  project?: string;
}) => {
  const loading = useRouteLoading();
  const router = useRouter();
  const { data: session, status } = useSession();
  const { data: user, isLoading } = useSWR<JSONSafe<User>>("/api/user", fetcherIgnore404);
  const [isSidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    "sidebar.collapsed",
    false
  );

  useEffect(() => {
    if (!user && !isLoading) {
      signIn();
    }
  }, [user, isLoading]);

  useEffect(() => {
    document.body.classList.add("no-overscroll");
    return () => document.body.classList.remove("no-overscroll");
  }, []);

  if (status === "loading") {
    return null;
  }

  if (!session?.user) {
    return null;
  }

  if (!user) {
    return null;
  }

  if (user?.onboarding === 0) {
    router.replace("/auth/onboarding?callbackUrl=" + encodeURIComponent("/dashboard"));
    return null;
  }

  if (!router.isReady) {
    return null;
  }

  applyFlags(user);

  return (
    <div className="AppShell flex min-h-screen w-full flex-col">
      <JournalDrawer sidebarCollapsed={isSidebarCollapsed} />
      {loading && <Loader />}
      <Sidebar collapsed={isSidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div
        className={`flex flex-col min-h-screen ${
          isSidebarCollapsed ? "sm:pl-14" : "sm:pl-44"
        }`}
      >
        {(header ?? true) && (
          <Header id={id} name={name} project={project} menuItem={menuItem} />
        )}
        <main className="grow flex flex-col justify-start">{children}</main>
      </div>
    </div>
  );
};
